import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody } from "@/components/PageChrome";
import { T } from "@/lib/testIds";
import { usePerms } from "@/lib/auth";
import PaginationBar from "@/components/PaginationBar";
import useClientPagination from "@/hooks/useClientPagination";

const MODULES = [
  "", "orders", "inventory", "accounts", "mapping", "warehouse",
  "users", "teams", "settings", "bank", "imports", "exports",
  "procurement", "inbound", "master", "listings", "1688", "price_update",
];
const ACTIONS = [
  "", "create", "update", "delete", "import", "export",
  "orders_import", "orders_scope_override",
  "sellerflex_orders", "sellerflex_orders_scope_override",
  "flipkart_orders", "flipkart_orders_scope_override",
  "marketplace_identifier", "shipping_update", "order_status_update",
  "amazon_shipping", "daily_shipping", "china_shipment",
];

export default function Logs() {
  const [rows, setRows] = useState([]);
  const [module, setModule] = useState("");
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [field, setField] = useState("");
  const [action, setAction] = useState("");
  const [recordId, setRecordId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [expanded, setExpanded] = useState(null);
  const { canView } = usePerms();

  // Feb 27 2026 — display UTC-stored ISO timestamps in IST (UTC+5:30) so ops
  // reads them in the same timezone they're working in. DB values stay ISO-UTC
  // (source of truth); this is presentation-only.
  const _fmtIST = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 19).replace("T", " ");
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    }).formatToParts(d).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
    return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute}:${parts.second}`;
  };

  // Feb 27 2026 — email → display-name lookup so the "User" column shows a
  // human-readable name (falls back to email when name is unavailable, e.g.
  // system events or deleted users).
  const emailToName = React.useMemo(() => {
    const m = {};
    for (const u of users || []) {
      if (u.email) m[u.email] = u.name || u.email;
    }
    return m;
  }, [users]);

  // Human-readable action labels so the Logs page reads clearly. Underlying
  // action strings on the log row (used for filtering / audit) stay untouched.
  const ACTION_LABELS = {
    import_create: "Created via import",
    import_update: "Updated via import",
    bulk_delete: "Bulk delete",
    orders_import: "Orders import job",
    flipkart_orders: "Flipkart import job",
    sellerflex_orders: "Sellerflex import job",
    sellerflex_shipping_update: "Sellerflex shipping update",
    order_status_update: "Order status update",
    orders_meta_update: "Orders meta update",
    backfill_allocations: "Backfill allocations",
    backfill_allocated_shipped: "Backfill allocated → Shipped",
    backfill_flipkart_shipped: "Backfill Flipkart → Shipped",
    release_procurement_allocation: "Release procurement",
  };

  const COLS = [
    { k: "timestamp", label: "Timestamp (IST)", field: "timestamp", render: (r) => <td className="font-mono whitespace-nowrap" title={r.timestamp}>{_fmtIST(r.timestamp)}</td> },
    { k: "user", label: "User", field: "user_email", render: (r) => (
      <td title={r.user_email || ""}>{emailToName[r.user_email] || r.user_email || "—"}</td>
    ) },
    { k: "module", label: "Module", field: "module", render: (r) => <td className="capitalize">{r.module}</td> },
    { k: "action", label: "Action", field: "action", render: (r) => (
      <td>
        <span
          className={`inline-block px-1.5 py-0.5 rounded-sm text-[10.5px] font-medium whitespace-nowrap ${
            /override|scope/.test(r.action) ? "bg-amber-100 text-amber-800" :
            r.action?.startsWith?.("import_") || /_import$|orders_import|flipkart_orders|sellerflex_orders/.test(r.action) ? "bg-indigo-100 text-indigo-800" :
            r.action === "create" ? "bg-emerald-100 text-emerald-800" :
            r.action === "delete" ? "bg-red-100 text-red-800" :
            r.action === "update" ? "bg-blue-100 text-blue-800" :
            "bg-slate-100 text-slate-700"
          }`}
          title={r.action}
        >{ACTION_LABELS[r.action] || r.action}</span>
      </td>
    ) },
    { k: "record", label: "Record", field: "record_id", render: (r) => <td className="font-mono truncate max-w-[160px]">{r.record_id || "—"}</td> },
    { k: "changes", label: "Changes", field: "changes", render: (r) => (
      <td>
        {(r.changes || []).slice(0, 3).map((c, i) => (
          <span key={i} className="inline-block mr-2 text-[11.5px]"><span className="text-slate-500">{c.field}:</span> <span className="text-red-700 line-through">{String(c.from ?? "—").slice(0, 20)}</span> → <span className="text-emerald-700">{String(c.to ?? "—").slice(0, 20)}</span></span>
        ))}
        {(r.changes || []).length > 3 && (
          <button
            className="text-[11px] text-blue-600 hover:underline"
            onClick={() => setExpanded(expanded === r.id ? null : r.id)}
            data-testid={`logs-expand-${r.id}`}
          >
            +{r.changes.length - 3} more
          </button>
        )}
      </td>
    ) },
  ];
  const visibleCols = COLS.filter((c) => canView("logs", c.field));
  const pag = useClientPagination(rows, "logs");

  const load = async () => {
    try {
      const params = {};
      if (module) params.module = module;
      if (userId) params.user_id = userId;
      if (field) params.field = field;
      if (action) params.action = action;
      if (recordId) params.record_id = recordId;
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await api.get("/logs", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [module, userId, field, action, recordId, start, end]);

  const inputCls = "h-8 px-2 text-[12.5px] border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500";

  return (
    <>
      <PageHeader title="Activity Logs" subtitle="Field-level audit trail across all modules" testId={T.moduleTitle("logs")} module="logs">
        <select value={module} onChange={(e) => setModule(e.target.value)} className={inputCls} data-testid="logs-module-filter">
          {MODULES.map((m) => <option key={m} value={m}>{m || "All modules"}</option>)}
        </select>
        <select value={action} onChange={(e) => setAction(e.target.value)} className={inputCls} data-testid="logs-action-filter">
          {ACTIONS.map((a) => <option key={a} value={a}>{a || "All actions"}</option>)}
        </select>
        <select value={userId} onChange={(e) => setUserId(e.target.value)} className={inputCls} data-testid="logs-user-filter">
          <option value="">All users</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
        </select>
        <input
          data-testid="logs-field-filter"
          value={field}
          onChange={(e) => setField(e.target.value)}
          placeholder="Field name"
          className={`${inputCls} w-32`}
        />
        <input
          data-testid="logs-record-filter"
          value={recordId}
          onChange={(e) => setRecordId(e.target.value)}
          placeholder="Record ID"
          className={`${inputCls} w-36`}
        />
        <input type="date" data-testid="logs-start-date" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} />
        <input type="date" data-testid="logs-end-date" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls} />
      </PageHeader>
      <PageBody>
        <div className="border border-slate-200 bg-white rounded-sm">
          <table className="erp-table">
            <thead>
              <tr>{visibleCols.map((c) => <th key={c.k}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {pag.pageRows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr>
                    {visibleCols.map((c) => <React.Fragment key={c.k}>{c.render(r)}</React.Fragment>)}
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={visibleCols.length} className="bg-slate-50 p-3">
                        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Full change list</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                          {(r.changes || []).map((c, i) => (
                            <div key={i} className="text-[11.5px] py-0.5">
                              <span className="text-slate-500 font-mono">{c.field}:</span>{" "}
                              <span className="text-red-700 line-through">{String(c.from ?? "—")}</span>{" → "}
                              <span className="text-emerald-700">{String(c.to ?? "—")}</span>
                            </div>
                          ))}
                        </div>
                        {r.after && r.action !== "update" && (
                          <>
                            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mt-3 mb-1">Payload</div>
                            <pre className="text-[11px] font-mono bg-white border border-slate-200 rounded-sm p-2 overflow-auto max-h-40">{JSON.stringify(r.after, null, 2)}</pre>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {!rows.length && <tr><td colSpan={visibleCols.length || 1} className="text-center py-8 text-slate-500">
                {visibleCols.length === 0 ? "No columns visible for your team." : "No log entries."}
              </td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-2 flex justify-end">
          <PaginationBar page={pag.page} pageSize={pag.pageSize} total={pag.total}
            onPageChange={pag.setPage} onPageSizeChange={pag.setPageSize} testIdPrefix="logs" />
        </div>
      </PageBody>
    </>
  );
}
